/**
 * The learning figures the app shows in more than one place.
 *
 * My Metrics repeats two numbers that already have a home page of their own:
 * CE credits claimed (/profile/ce-transcript) and series completion (the Series
 * page). Each rule lives here once and those pages call it too, so the tile and
 * the page it links to cannot drift apart. Unclaimed credits live here too: My
 * Metrics counts them on a tile and lists them in a modal.
 *
 * Everything here is pure: plain data in, plain data out, no RootState and no
 * reading the clock. Callers pass `now` themselves.
 */

// --- Shared shapes -----------------------------------------------------------

/**
 * One entry of GET /api/enrichment_center/time_watched, keyed by content
 * record id. Timestamps are ISO-8601 strings.
 *   created_at        - the user's most recent playback log for the item
 *   video_finished_at - the user's first 'video ended' / 'audio ended' log
 *   video_claimed     - a cme_credit exists for the item
 */
export interface TimeWatchedEntry {
  seconds_watched?: number;
  max_seconds_watched?: number;
  created_at?: string | null;
  video_finished?: boolean;
  video_claimed?: boolean;
  video_finished_at?: string | null;
  video_claimed_at?: string | null;
}

/** The enrichment_center `timeWatched` map, as the slice stores it. */
export type TimeWatchedMap = Readonly<Record<string | number, unknown>>;

/** Entries arrive untyped from the slice; anything that isn't an object is no entry at all. */
const asTimeWatchedEntry = (value: unknown): TimeWatchedEntry | undefined =>
  typeof value === 'object' && value !== null ? (value as TimeWatchedEntry) : undefined;

export const timeWatchedFor = (timeWatched: TimeWatchedMap | null | undefined, recordId: string | number) =>
  asTimeWatchedEntry(timeWatched?.[recordId]);

// --- CE transcript -----------------------------------------------------------

export interface TranscriptCredit {
  title_of_activity: string;
  cme_certificate_awarded_at: string;
  certificate_type?: unknown;
}

/**
 * The transcript page's default "From" date: the same calendar date one year
 * before `now`, at the same time of day. The page shows this in its date
 * picker; `transcriptCredits` rounds it down to local midnight.
 *
 * Date#setFullYear semantics apply, so from Feb 29 it lands on Mar 1.
 */
export const oneYearBefore = (now: Date): Date => {
  const start = new Date(now);
  start.setFullYear(start.getFullYear() - 1);
  return start;
};

/**
 * The credits the CE transcript lists for a From/To selection.
 *
 * - The window runs from local midnight on `startDate` through 23:59:59.999
 *   local on `endDate`, inclusive at both ends, compared against
 *   `cme_certificate_awarded_at`. A null bound leaves that side open.
 * - Every certificate type counts; nothing is filtered by type.
 * - A title claimed more than once under the same certificate type is listed
 *   once, keeping the most recently awarded claim (the first seen wins a tie).
 *
 * Order is first appearance of each title/type pair; the page sorts afterwards.
 */
export const transcriptCredits = <T extends TranscriptCredit>(
  credits: readonly T[],
  startDate: Date | null,
  endDate: Date | null,
): T[] => {
  const start = startDate ? new Date(startDate) : null;
  if (start) start.setHours(0, 0, 0, 0);
  const end = endDate ? new Date(endDate) : null;
  if (end) end.setHours(23, 59, 59, 999);

  const inWindow = credits.filter((credit) => {
    const awardedAt = new Date(credit.cme_certificate_awarded_at);
    if (start && awardedAt < start) return false;
    if (end && awardedAt > end) return false;
    return true;
  });

  const latestByActivity = new Map<string, T>();
  inWindow.forEach((credit) => {
    const key = `${credit.title_of_activity}|${credit.certificate_type}`;
    const kept = latestByActivity.get(key);
    if (!kept || new Date(credit.cme_certificate_awarded_at) > new Date(kept.cme_certificate_awarded_at)) {
      latestByActivity.set(key, credit);
    }
  });
  return Array.from(latestByActivity.values());
};

/**
 * CE credits claimed, exactly as /profile/ce-transcript counts them when it
 * opens: its default window of one year back through the end of today.
 * Every listed certificate is worth 1 CE, so this is also the CE total.
 */
export const ceTranscriptCount = <T extends TranscriptCredit>(credits: readonly T[], now: Date): number =>
  transcriptCredits(credits, oneYearBefore(now), now).length;

// --- Unclaimed credits -------------------------------------------------------

export interface UnclaimedCandidate {
  record_id_: string | number;
  cme_enabled?: unknown;
  time_watched?: unknown;
}

/** A cme_credits row, as far as matching it to content goes. */
export interface CreditRecord {
  lesson_number?: unknown;
}

/**
 * Whether the user holds a CE credit for the item with this record id: the
 * time_watched entry says video_claimed, or a cme_credits row's lesson_number
 * is the record id. The second is the same join the API uses to compute
 * video_claimed (cme_credits.lesson_number = the log's context), so it also
 * catches a claim made in this session, which the time_watched cache never
 * hears about.
 */
export const holdsCredit = (
  recordId: string | number,
  timeWatched: TimeWatchedEntry | undefined,
  cmeCredits: readonly CreditRecord[] = [],
): boolean => {
  const id = String(recordId);
  return timeWatched?.video_claimed === true ||
    cmeCredits.some((credit) => credit.lesson_number != null && String(credit.lesson_number) === id);
};

/**
 * The unclaimed-credits predicate: a CE-eligible item the user has
 * finished and holds no credit for. All-time; there is no date window.
 *
 * Pass the user's loaded cme_credits. They are the whole record of what was
 * claimed, so with them an item is unclaimed exactly when no credit matches
 * it, including items finished or replayed in this session, whose time_watched
 * entries the players rewrite without video_claimed.
 *
 * Without them (cmeCredits undefined), only an entry the server itself marked
 * video_claimed: false counts: an entry missing the flag was rewritten in this
 * session, and whether it was claimed can't be known from it.
 */
export const isUnclaimedCredit = (item: UnclaimedCandidate, cmeCredits?: readonly CreditRecord[]): boolean => {
  const tw = asTimeWatchedEntry(item.time_watched);
  if (tw == null || !item.cme_enabled || tw.video_finished !== true) return false;
  if (holdsCredit(item.record_id_, tw, cmeCredits)) return false;
  return cmeCredits !== undefined || tw.video_claimed === false;
};

/**
 * The items My Metrics' unclaimed-credits modal lists, most recently finished first.
 * Takes content already joined with its time_watched entry
 * (`getContentWithTimeWatched`) and, to be exact within a session, the user's
 * loaded cme_credits (see isUnclaimedCredit). Each item is 1 unclaimed CE.
 *
 * An entry the players rewrote in this session has lost video_finished_at, so
 * it sorts by its latest playback time instead (for an item just finished,
 * when it ended).
 */
export const unclaimedCreditItems = <T extends UnclaimedCandidate>(
  contentWithTimeWatched: readonly T[],
  cmeCredits?: readonly CreditRecord[],
): T[] => {
  const finishedAt = (item: T) => {
    const tw = asTimeWatchedEntry(item.time_watched);
    const time = parseActivityTime(tw?.video_finished_at ?? tw?.created_at);
    return Number.isFinite(time) ? time : 0;
  };
  return contentWithTimeWatched
    .filter((item) => isUnclaimedCredit(item, cmeCredits))
    .sort((a, b) => finishedAt(b) - finishedAt(a));
};

// --- Series ------------------------------------------------------------------

export interface SeriesSection {
  record_id_?: string | number;
  section_name: string;
  show: boolean | number;
  ec_contents?: readonly number[];
}

export interface SeriesContent {
  id: number;
  record_id_: string | number;
  content_type?: string;
}

const isHandout = (item: SeriesContent) => item.content_type?.toLowerCase() === 'handout';

/** First item per id, matching Array#find over the same list. */
const indexContentById = <C extends SeriesContent>(content: readonly C[]): Map<number, C> => {
  const byId = new Map<number, C>();
  content.forEach((item) => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });
  return byId;
};

const itemsOfSection = <C extends SeriesContent>(section: SeriesSection, contentById: Map<number, C>): C[] =>
  (section.ec_contents ?? [])
    .map((id) => contentById.get(id))
    .filter((item): item is C => !!item && !isHandout(item));

/**
 * The items a series shows on the Series page, in section order: the section's
 * content that is actually loaded for this user (the API only returns content
 * the user may see, so an id missing from `content` is hidden), minus handouts.
 */
export const seriesItems = <C extends SeriesContent>(section: SeriesSection, content: readonly C[]): C[] =>
  itemsOfSection(section, indexContentById(content));

export interface SeriesCompletion {
  finished: number;
  total: number;
  percent: number;
}

const isFinished = (item: SeriesContent, timeWatched: TimeWatchedMap | null | undefined) =>
  Boolean(timeWatchedFor(timeWatched, item.record_id_)?.video_finished);

/**
 * The Series page completion figure for a list of items: an item counts once
 * its time_watched entry says video_finished, and the percent is rounded to a
 * whole number. An empty list is 0%.
 */
export const seriesCompletion = (
  items: readonly SeriesContent[],
  timeWatched: TimeWatchedMap | null | undefined,
): SeriesCompletion => {
  const total = items.length;
  const finished = items.filter((item) => isFinished(item, timeWatched)).length;
  const percent = total === 0 ? 0 : Math.round((finished / total) * 100);
  return { finished, total, percent };
};

export type WithTimeWatched<C> = C & { time_watched: TimeWatchedEntry | undefined };

export interface SeriesProgress<C extends SeriesContent = SeriesContent> extends SeriesCompletion {
  sectionId: string | number | undefined;
  name: string;
  /** Latest time_watched created_at / video_finished_at across the series' items, ISO-8601; null if none. */
  lastActivityAt: string | null;
  /** The series' unfinished items in section order, each with its time_watched entry (if any). */
  remaining: Array<WithTimeWatched<C>>;
}

/** A zone-less 'YYYY-MM-DD HH:MM:SS(.sss)' timestamp. */
const ZONELESS_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/;

/**
 * Milliseconds for a time_watched timestamp. The API sends ISO strings with a
 * zone, but the players write in-session entries as UTC wall time with the 'Z'
 * cut off ('2026-09-26 12:00:00.000'), which Date.parse would read as local
 * time and shift by the viewer's offset. Those are read as UTC.
 */
export const parseActivityTime = (stamp: string | null | undefined): number => {
  if (!stamp) return Number.NaN;
  const zoneless = ZONELESS_TIMESTAMP.exec(stamp.trim());
  return zoneless ? Date.parse(`${zoneless[1]}T${zoneless[2]}Z`) : Date.parse(stamp);
};

const latestActivity = (items: readonly SeriesContent[], timeWatched: TimeWatchedMap | null | undefined): string | null => {
  let latest = Number.NEGATIVE_INFINITY;
  items.forEach((item) => {
    const tw = timeWatchedFor(timeWatched, item.record_id_);
    [tw?.created_at, tw?.video_finished_at].forEach((stamp) => {
      const time = parseActivityTime(stamp);
      if (time > latest) latest = time;
    });
  });
  return Number.isFinite(latest) ? new Date(latest).toISOString() : null;
};

/**
 * Completion for every series the Series page shows, in the order given.
 *
 * Only sections the Series page renders are included: `show` is true and at
 * least one visible, non-handout item remains, so `total` is never 0.
 * finished / total / percent are exactly the page's figures (`seriesCompletion`
 * over `seriesItems`).
 */
export const seriesProgress = <C extends SeriesContent>(
  sections: readonly SeriesSection[],
  content: readonly C[],
  timeWatched: TimeWatchedMap | null | undefined,
): Array<SeriesProgress<C>> => {
  const contentById = indexContentById(content);
  return sections
    .filter((section) => section.show)
    .map((section) => {
      const items = itemsOfSection(section, contentById);
      return {
        sectionId: section.record_id_,
        name: section.section_name,
        ...seriesCompletion(items, timeWatched),
        lastActivityAt: latestActivity(items, timeWatched),
        remaining: items
          .filter((item) => !isFinished(item, timeWatched))
          .map((item) => ({ ...item, time_watched: timeWatchedFor(timeWatched, item.record_id_) })),
      };
    })
    .filter((series) => series.total > 0);
};

/** Most recent activity first; series with no activity last; otherwise input order. */
const byRecentActivity = (a: SeriesProgress<SeriesContent>, b: SeriesProgress<SeriesContent>) => {
  const aTime = a.lastActivityAt ? Date.parse(a.lastActivityAt) : Number.NEGATIVE_INFINITY;
  const bTime = b.lastActivityAt ? Date.parse(b.lastActivityAt) : Number.NEGATIVE_INFINITY;
  if (aTime === bTime) return 0;
  return bTime > aTime ? 1 : -1;
};

/** Series shown as partway through (0 < percent < 100), most recent activity first, at most `limit`. */
export const seriesInProgress = <C extends SeriesContent>(
  progress: ReadonlyArray<SeriesProgress<C>>,
  limit = 3,
): Array<SeriesProgress<C>> =>
  progress
    .filter((series) => series.percent > 0 && series.percent < 100)
    .sort(byRecentActivity)
    .slice(0, limit);

export interface NextUpCandidate extends SeriesContent {
  cme_enabled?: unknown;
}

export interface NextUp<C extends NextUpCandidate> {
  series: SeriesProgress<C>;
  item: WithTimeWatched<C>;
  /** Finishing the item would make it an unclaimed credit: CE-eligible and no credit held for it. */
  earnsClaimableCe: boolean;
}

/**
 * The series with exactly one visible, non-handout item left (the most
 * recently active one if several), and that item. Null when no series is one
 * item from done.
 *
 * `earnsClaimableCe` mirrors the unclaimed-credits predicate the item would
 * meet once finished (given the loaded cme_credits): cme_enabled, and no
 * credit already held for it (`holdsCredit`).
 */
export const nextUp = <C extends NextUpCandidate>(
  progress: ReadonlyArray<SeriesProgress<C>>,
  cmeCredits: readonly CreditRecord[] = [],
): NextUp<C> | null => {
  const [series] = progress.filter((candidate) => candidate.remaining.length === 1).sort(byRecentActivity);
  if (!series) return null;

  const [item] = series.remaining;
  const creditHeld = holdsCredit(item.record_id_, item.time_watched, cmeCredits);
  return { series, item, earnsClaimableCe: Boolean(item.cme_enabled) && !creditHeld };
};
